import { connect, useDispatch } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";

import { State } from "redux/store";
import { fetchHealthStatus } from "redux/Datasets/actions";
import { isSharingEnabled } from "redux/SingleDataset/selectors";
import { isAnonymousUser, selectUser, userLogout } from "redux/authReducer";
import { getUserInfo } from "redux/User/selectors";

import ToastWrapper from "views/components/ToastWrapper";
import Cart from "views/components/Cart";
import Loader from "views/uikit/Loader";
import Sidebar from "./Sidebar";
import ReactGA from "react-ga4";
import { INVITATION_TOKEN_PARAMETER } from "helpers/constants/miscellaneous";
import { isFastdupUser } from "helpers/utility/utilities";
import { PUBLIC_DATASETS_PATH } from "helpers/constants/paths";
import styles from "./style.module.scss";
import DatasetSharingMenu from "views/components/SharingMenu/DatasetSharingMenu";
import { acceptInvitation } from "redux/SingleDataset/actions";

import {
  LinkedInConversionId,
  trackLinkedInConversion,
} from "helpers/utility/linkedinAnalytics";
import { getShowBugReportModal } from "redux/Modals/selectors";
import classNames from "classnames";
import { ConfigProvider } from "../providers/ConfigProvider";
import { UserConfigProvider } from "../../contexts/UserConfigContext";

function Layout({
  userAvatar,
  isSharingEnabled,
  showBugReporter,
}: LayoutTypes) {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.setAttribute("data-flat-response", String(false));
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { datasetId } = useParams();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const isPublicPath = location.pathname === PUBLIC_DATASETS_PATH;

  const grantAccessForInvite = async () => {
    const invitationToken = searchParams.get(INVITATION_TOKEN_PARAMETER);
    if (datasetId && invitationToken) {
      const user = selectUser();
      if ((user && isAnonymousUser()) || !user) {
        // If user is not logged in we can't grant them access, so we prompt them to sign in.
        userLogout();
      }
      await dispatch(acceptInvitation(datasetId, invitationToken) as any);
      searchParams.delete(INVITATION_TOKEN_PARAMETER);
      navigate({ search: `?${searchParams.toString()}` }, { replace: true });
    }
  };

  const reportAnalytics = () => {
    if (searchParams.get("lin") === "1") {
      searchParams.delete("lin");
    }
    if (searchParams.get("nuse") === "1") {
      trackLinkedInConversion(LinkedInConversionId.SignUp);
      ReactGA.event({ category: "User", action: "Sign Up" });
      searchParams.delete("nuse");
    }
    navigate({ search: `?${searchParams.toString()}` });
  };

  useEffect(() => {
    const initializeLayout = async () => {
      reportAnalytics();
      await grantAccessForInvite();
      const authenticationDisabled =
        process.env.REACT_APP_IS_AUTHENTICATION_DISABELED?.trim() === "false";
      console.log(
        "auth",
        process.env.REACT_APP_IS_AUTHENTICATION_DISABELED === "false",
      );
      const fastdupUser = isFastdupUser();

      if (
        !datasetId &&
        isAnonymousUser() &&
        !isPublicPath &&
        authenticationDisabled &&
        !fastdupUser
      ) {
        userLogout();
      }
      setLoading(false);
    };
    initializeLayout();

    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    const repeatedHealthCheck = setInterval(() => {
      dispatch(fetchHealthStatus() as any);
    }, 60000);

    dispatch(fetchHealthStatus() as any);

    return () => clearInterval(repeatedHealthCheck);
  }, [dispatch]);

  useEffect(() => {
    document.body.setAttribute("data-theme", "dark");
    localStorage.setItem("theme", "dark");
  }, []);

  if (!loading) {
    return (
      <UserConfigProvider>
        <ConfigProvider>
          <ToastWrapper />
          <div className={styles.mainWrapper}>
            <div className={styles.innerWrapper}>
              <Sidebar
                userAvatar={userAvatar}
                showBugReporter={showBugReporter}
              />
              <div className={styles.content}>
                <div className={classNames(styles.body)}>
                  <Outlet />
                </div>
              </div>
            </div>
          </div>
          <Cart />
          {isSharingEnabled && <DatasetSharingMenu />}
        </ConfigProvider>
      </UserConfigProvider>
    );
  }
  return (
    <div className={styles.loadingPage}>
      <Loader />
    </div>
  );
}

const mapStateToProps = (state: State) => {
  const userInfo = getUserInfo(state);
  return {
    userAvatar: userInfo.userAvatar,
    isSharingEnabled: isSharingEnabled(state),
    showBugReporter: getShowBugReportModal(state),
  };
};

export default connect(mapStateToProps)(Layout);

type LayoutTypes = {
  userAvatar?: string;
  isSharingEnabled: boolean;
  showBugReporter: boolean;
};
